import { useMemo, useRef, useCallback, useState } from 'preact/hooks';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { SelectionDetail, ListItemRendererContext } from '#UNSAFE_Collection';
import { getViewportConfig } from '#utils/PRIVATE_collectionUtils';
import { FlattenedTreeNode, InternalTreeNode, FlatTreeViewProps as Props } from './TreeView.types';
import { LoadMoreTreeCollection } from './LoadMoreTreeCollection';
import {
  useSelection,
  useAdditiveSelection,
  SelectionRangeDetail
} from '#hooks/PRIVATE_useSelection';
import { useExpandCollapse } from '#hooks/PRIVATE_useListCommon';
import { keyExtractor, getPrevNextKey, findElementByKey } from '#utils/PRIVATE_collectionUtils';
import { addKeys, containsKey } from '#utils/UNSAFE_keys';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { TreeViewRedwoodTheme } from './themes/redwood/TreeViewTheme.css';
import { useTreeNavigation } from './useTreeNavigation';
import { Selector } from '#UNSAFE_Selector';
import { isMobile } from './TreeItem';
import { SkeletonContainer } from './SkeletonContainer';
import { TreeItemSkeletons } from './TreeItemSkeletons';
import { LiveRegion } from '#UNSAFE_LiveRegion';
import { HiddenAccessible } from '#UNSAFE_HiddenAccessible';
import { useId } from '#hooks/UNSAFE_useId';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { TREE_ITEM_SELECTOR } from './TreeViewUtils';

/**
 * A FlatTreeView displays flattened data as a tree with highly interactive features.
 */
export function FlatTreeView<K extends string | number, D>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  data,
  selectionMode = 'none',
  selectedKeys = { all: false, keys: new Set() },
  onSelectionChange,
  onToggle,
  viewportConfig,
  ...rest
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [liveRegion, setLiveRegion] = useState<string>();
  const { collection_loading, collection_loaded } = useTranslationBundle<BundleType>(
    '@oracle/oraclejet-preact'
  );
  const loadingDescId = useId();

  const isExpanded = useCallback((key: K) => {
    if (rootRef.current) {
      const elem = findElementByKey(rootRef.current, key, TREE_ITEM_SELECTOR);
      return elem?.getAttribute('aria-expanded') === 'true';
    }
    return false;
  }, []);

  const { treeNavigationProps, currentKey, showFocusRing } = useTreeNavigation(
    data,
    rootRef,
    isExpanded,
    idExtractor
  );

  const items: InternalTreeNode<K, D>[] = useMemo(
    () =>
      data?.map((value: FlattenedTreeNode<K, D>) => {
        const itemKey = value.metadata.key;
        const newValue = { ...value } as InternalTreeNode<K, D>;
        newValue.metadata.selected = containsKey(selectedKeys, itemKey);
        newValue.metadata.hasFocus = currentKey === itemKey && showFocusRing;
        return newValue;
      }) || [],
    [data, selectedKeys, currentKey, showFocusRing]
  );

  const expandCollapseProps = useExpandCollapse(
    (element: HTMLElement) => {
      if (element.getAttribute('role') === 'tree') {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, TREE_ITEM_SELECTOR) as K;
    },
    isExpanded,
    onToggle,
    true
  );

  // tracking the anchor key which is used for shift+click selection
  const anchorKey = useRef<K | undefined>();

  const handleSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      if (onSelectionChange) {
        if (detail.value.all === false && detail.value.keys.size > 0) {
          // during regular selection, the last key selected by a user gesture is the anchor key
          anchorKey.current = Array.from(detail.value.keys.values()).pop();
        }
        onSelectionChange(detail);
      }
    },
    [anchorKey, onSelectionChange]
  );

  const handleSelectionRangeChange = useCallback(
    (detail: SelectionRangeDetail<K>) => {
      if (data && onSelectionChange) {
        const value = handleSelectionRange(detail, data);
        if (Array.isArray(value)) {
          if (detail.eventType === 'shiftSpace') {
            // during range selection by shift+space, the first key selected by a user gesture is the anchor key
            anchorKey.current = detail.value.start;
          } else {
            // during range selection by shift+click/arrow keys, the last key selected by a user gesture is the anchor key
            anchorKey.current = detail.value.end;
          }
          if (selectionMode === 'multipleToggle') {
            const keys = addKeys(selectedKeys, value);
            onSelectionChange({ value: keys, target: null });
          } else {
            onSelectionChange({ value: { all: false, keys: new Set(value) }, target: null });
          }
        }
      }
    },
    [data, selectedKeys, onSelectionChange]
  );

  // single or multiple selection mode
  const { selectionProps } = useSelection(
    (element: HTMLElement) => {
      if (element === rootRef.current) {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, TREE_ITEM_SELECTOR) as K;
    },
    selectedKeys,
    selectionMode === 'multipleToggle' ? 'none' : selectionMode,
    false,
    'replace',
    handleSelectionChange,
    anchorKey.current,
    currentKey,
    (currentKey: K | undefined, isPrev: boolean) =>
      getPrevNextKey(rootRef.current, currentKey, isPrev, TREE_ITEM_SELECTOR),
    undefined,
    (key: K) => {
      if (rootRef.current) {
        const elem = findElementByKey(rootRef.current, key, TREE_ITEM_SELECTOR);
        if (elem) {
          elem.scrollIntoView({ block: 'nearest' });
        }
      }
    },
    handleSelectionRangeChange
  );

  // multipleToggle selection mode
  const { eventProps: multipleToggleSelectionProps } = useAdditiveSelection(
    (element: HTMLElement) => {
      if (element === rootRef.current) {
        return currentKey === undefined ? null : currentKey;
      }
      return keyExtractor(element, TREE_ITEM_SELECTOR) as K;
    },
    selectedKeys,
    selectionMode === 'multipleToggle' ? 'multiple' : 'none',
    false,
    (key: K) => {
      if (data) {
        return data.findIndex((value: FlattenedTreeNode<K, D>) => value.metadata.key === key);
      }
      return -1;
    },
    (startIndex: number, endIndex: number) => {
      if (data) {
        const keys = data.map((value: FlattenedTreeNode<K, D>) => value.metadata.key);
        return keys.slice(startIndex, endIndex);
      }
      return [];
    },
    (currentKey: K | undefined, isPrev: boolean) =>
      getPrevNextKey(rootRef.current, currentKey, isPrev, TREE_ITEM_SELECTOR),
    currentKey,
    handleSelectionChange,
    handleSelectionRangeChange
  );

  //Note: onKeyUp doesn't work with the cmd key
  const selectAllWithKeyboard = useCallback(
    (event: KeyboardEvent) => {
      if (selectionMode === 'multiple' || selectionMode === 'multipleToggle') {
        if (onSelectionChange && (event.metaKey || event.ctrlKey) && event.key === 'a') {
          event.preventDefault();
          onSelectionChange({
            value: { all: true, deletedKeys: new Set() },
            target: event.target
          });
        }
      }
    },
    [onSelectionChange, selectionMode]
  );

  const { classes } = useComponentTheme(TreeViewRedwoodTheme);
  viewportConfig = getViewportConfig(rootRef, viewportConfig);

  const updateContextCallback = useCallback(
    (context: ListItemRendererContext<K, D>) => {
      const key = context.metadata.key;
      context.selector =
        selectionMode === 'none' || selectionMode === 'single'
          ? undefined
          : (ariaLabel?: string) => (
              <div aria-hidden={isMobile() ? undefined : true}>
                <Selector
                  aria-label={isMobile() ? ariaLabel : undefined}
                  rowKey={key}
                  selectedKeys={selectedKeys}
                  onChange={onSelectionChange}
                />
              </div>
            );
    },
    [selectionMode, selectedKeys, onSelectionChange]
  );

  const updateLiveRegion = useCallback(() => {
    setLiveRegion(collection_loading());
  }, [collection_loading]);

  return (
    <>
      <div
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={
          data == null
            ? ariaDescribedBy
              ? [...new Set(ariaDescribedBy.split(/\s+/).concat(loadingDescId))].join(' ')
              : loadingDescId
            : ariaDescribedBy
        }
        ref={rootRef}
        role="tree"
        aria-multiselectable={selectionMode === 'multiple' ? true : false}
        tabIndex={0}
        class={classes}
        {...mergeProps(
          treeNavigationProps,
          selectionProps,
          multipleToggleSelectionProps,
          expandCollapseProps,
          { onKeyDown: selectAllWithKeyboard }
        )}>
        {data == null ? (
          defaultLoadingIndicator(updateLiveRegion)
        ) : (
          <LoadMoreTreeCollection
            data={items}
            selectionMode={selectionMode}
            onToggle={onToggle}
            viewportConfig={viewportConfig}
            updateContext={updateContextCallback}
            {...rest}></LoadMoreTreeCollection>
        )}
      </div>
      <HiddenAccessible id={loadingDescId}>{collection_loading()}</HiddenAccessible>
      <LiveRegion>
        {data == null ? liveRegion : liveRegion === collection_loading() ? collection_loaded() : ''}
      </LiveRegion>
    </>
  );
}

const defaultLoadingIndicator = (callback: () => void) => {
  return (
    <SkeletonContainer minimumCount={8} onSkeletonVisible={callback}>
      {() => {
        return <TreeItemSkeletons />;
      }}
    </SkeletonContainer>
  );
};

const idExtractor = (elem: Element) => elem.id;

/**
 * A helper function to handle range selection
 */
export const handleSelectionRange = <K, D>(
  detail: SelectionRangeDetail<K>,
  data: FlattenedTreeNode<K, D>[]
) => {
  const keys = data.map((value: FlattenedTreeNode<K, D>) => {
    return value.metadata.key;
  });
  const startIndex = keys.indexOf(detail.value.start);
  const endIndex = keys.indexOf(detail.value.end);
  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);
  if (minIndex === -1) {
    // return a range to fetch so we can find all the keys, maxIndex should not be -1
    return { offset: 0, count: -1 };
  } else {
    // range of keys are in the current viewport, return them
    return keys.slice(minIndex, maxIndex + 1);
  }
};
